import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type Href,
  type RouteParams,
  type RoutePath,
  router,
  useFocusEffect,
  useLocalSearchParams,
  useNavigation,
  useRouter,
} from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';
import {
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  type ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { useSharedValue } from 'react-native-reanimated';

import { type FlashListRef } from '@shopify/flash-list';

import { useA11ySelector } from '@sg/a11y-helpers';
import {
  FadeView,
  LoadingAnimation,
  ScreenHeader as GarnishScreenHeader,
  theme,
  useResponsive,
  useScrollspyActiveTargetId,
  useVirtualizedScrollspy,
} from '@sg/garnish';
import { trackRadarMenuViewed } from '@sg/radar-fe';
import { useDietaryRestrictions } from '@sg/shared-fe-features-menu';

import {
  MenuCategoriesGrid,
  MenuFloatingButtons,
  MenuOnlineOrderingUnavailable,
} from '@order/components-menu';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useDeliveryDisclosure } from '@order/shared-hooks';
import {
  telemetryLocationFromInput,
  telemetryProductFromInput,
  useTelemetry,
  useTrackEventOnFocus,
} from '@order/Telemetry';

import { useBraze } from '../../../../context/Braze';
import { useDietaryPreferencesMachine } from '../../../../context/DietaryPreferences';
import { useSetLastInteractedMenuStore } from '../../../../context/LastInteractedStore';
import { useCart } from '../../../../hooks/useCart/useCart';
import { useCustomScreenTitle } from '../../../../hooks/useCustomScreenTitle';
import { useDeliveryOrderInFlight } from '../../../../hooks/useDeliveryOrderInFlight';
import { useMenuReorderRailOrders } from '../../../../hooks/useMenuReorderRailOrders';
import { useOneClickAddToBag } from '../../../../hooks/useOneClickAddToBag';
import { useReorderIfRequired } from '../../../../hooks/useReorderIfRequired';
import { useDietaryPreferencesState } from '../../../DietaryRestrictions';
import { MENU_VIEWS } from '../../../NavbarV2/NavbarV2.constant';
import { ScreenHeader } from '../../../ScreenHeader';
import {
  useDerivedRestaurantData,
  type useMenuContent,
  useMenuContentNavigation,
  useMenuLineageKeyCopyMode,
  type useProductDetailsContentData,
} from '../../hooks';
import {
  type MenuContentDeliveryDetailsData,
  type MenuContentOrderChannel,
  type MenuContentProduct,
  type MenuContentRestaurantData,
} from '../../MenuContent.types';
import {
  buildMenuContentListRows,
  getMenuContentListRowTargetId,
  type MenuContentListRow,
} from '../../utils';
import { MenuContentCategories } from '../MenuContentCategories';
import {
  MenuContentDietaryPreferencesModal,
  useMenuContentDietaryPreferencesModal,
} from '../MenuContentDietaryPreferencesModal';
import { MenuContentHeader } from '../MenuContentHeader';
import { MenuContentNavigationHeader } from '../MenuContentNavigationHeader';
import { MenuContentProductCard } from '../MenuContentProductCard';
import { MenuContentTopDownModal } from '../MenuContentTopDownModal';
import { MenuContentTopDownView } from '../MenuContentTopDownView';

// ─────────────────────────────────────────────────────────────────────────────

export const MenuContentReady = memo((props: MenuContentReadyProps) => {
  const navigation = useNavigation();
  const localParams = useLocalSearchParams<MenuRoute>();

  // ─── Derived ─────────────────────────────────────────────────────────

  const routes = navigation.getState()?.routes ?? [];
  const menuScreensCount = routes.filter((route) => MENU_SCREEN_NAMES.some((name) => name === route.name)).length; // prettier-ignore
  const hasMultipleMenuScreens = menuScreensCount > 1;

  // NOTE: Stash params in a ref so the layout effect picks up the current
  //       screen's params without re-firing on every render.
  const localParamsRef = useRef(localParams);
  localParamsRef.current = localParams;

  // ─── Helpers ─────────────────────────────────────────────────────────

  /**
   * Ensures the customer only has a single active menu screen in the stack by
   * dismissing any others and re-anchoring to the current menu's href.
   */
  const dismissOtherMenuScreens = useCallback(() => {
    if (!hasMultipleMenuScreens) return;

    if (router.canDismiss()) {
      router.dismissAll();
    }

    const href = getMenuHref(localParamsRef.current);

    router.replace(href);
  }, [hasMultipleMenuScreens]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(dismissOtherMenuScreens, [dismissOtherMenuScreens]);

  // ─────────────────────────────────────────────────────────────────────

  // While the layout effect is dismissing + replacing, render loading state.
  if (hasMultipleMenuScreens) {
    return <LoadingAnimation />;
  }

  return <Content {...props} />;
});

// ─────────────────────────────────────────────────────────────────────────────

export const Content = (props: MenuContentReadyProps) => {
  const {
    restaurantData,
    deliveryDetailsData,
    orderChannel,
    locationName,
    productDetailsContentData,
    categoriesBadges,
    caloriesOverrides,
    reorderRailOrders,
  } = props;

  // ─────────────────────────────────────────────────────────────────────

  const { minWidth, currentBreakpoint } = useResponsive();
  const { formatMessage } = useIntl();
  const { lineItemsCount } = useCart();
  const { trackEvent, track } = useTelemetry();
  const { logCustomEvent } = useBraze();
  const router = useRouter();
  const isLineageKeyCopyModeEnabled = useMenuLineageKeyCopyMode();
  const { deliveryDisclosure } = useDeliveryDisclosure({
    restaurantDeliveryFee: restaurantData.deliveryFee,
  });

  const locationHeaderA11ySelector = useA11ySelector('menu.location-header');

  const routeParams = useLocalSearchParams<MenuSearchParams>();

  const { referrer, view } = routeParams ?? {};

  // NOTE: Scrollspy updates the `category` route param after mount, so we
  // capture the initial value to detect whether the user deep-linked into a
  // category via URL anchor (e.g. `/menu/abc?category=wraps`).
  const hadInitialCategoryAnchorRef = useRef(Boolean(routeParams.category));

  // ─────────────────────────────────────────────────────────────────────

  const { isAcceptingOrders = true, notAcceptingOrdersReason } = restaurantData;

  const {
    restaurantId,
    restaurantName,
    restaurantSlug,
    categories: filteredCategories,
  } = useDerivedRestaurantData({ restaurantData, orderChannel });

  const menuTelemetryProducts = useMemo(
    () =>
      filteredCategories.flatMap((category) =>
        category.products.map((product) =>
          telemetryProductFromInput({ ...product, location: restaurantSlug }),
        ),
      ),
    [filteredCategories, restaurantSlug],
  );

  // ─── Flags ───────────────────────────────────────────────────────────

  const isTopDownCategoriesModalEnabled = useFeatureFlag(
    'CELS-3094-menu-top-down-categories-enabled',
  );
  const isNavbarV2Enabled = useFeatureFlag('CELS-5440-order-navbar-v2-enabled');
  const isMenuRedesignEnabled = useFeatureFlag('CELS-5649-order-menu-redesign-enabled');

  const shouldRenderReorderRailSection = reorderRailOrders.mostRecentLineItems.length > 0;
  const shouldShowBagFloatingButton =
    currentBreakpoint.isXS && lineItemsCount > 0 && !isNavbarV2Enabled;

  const shouldRenderLegacyTopDownCategories =
    referrer !== 'bag' &&
    referrer !== 'reorder-confirm-location' &&
    isTopDownCategoriesModalEnabled &&
    currentBreakpoint.isXS &&
    !hadInitialCategoryAnchorRef.current;

  const shouldRenderMenuRedesign = isMenuRedesignEnabled && currentBreakpoint.isXS;
  const disclaimer =
    isNavbarV2Enabled &&
    isMenuRedesignEnabled &&
    orderChannel === 'delivery' &&
    restaurantData.showDeliveryFeeDisclosure
      ? deliveryDisclosure
      : undefined;
  const shouldShowTopDownCategoriesViewInitially = shouldRenderMenuRedesign && !hadInitialCategoryAnchorRef.current; // prettier-ignore
  const shouldRenderTopDownCategoriesModal = shouldRenderLegacyTopDownCategories && !shouldRenderMenuRedesign; // prettier-ignore

  // ─── State ───────────────────────────────────────────────────────────

  const {
    hasOpenedDietaryPreferences,
    setHasOpenedDietaryPreferencesState,
    isResolvingDietaryPreferencesState,
  } = useDietaryPreferencesState();

  const { restrictions } = useDietaryPreferencesMachine({ referrer: 'Menu' });
  const getRestrictionNotice = useDietaryRestrictions({ restrictions });

  const onOneClickAddToBagSuccess = useCallback<OnOneClickAddToBagSuccess>(
    (selectedProduct) => {
      track('menu.one_click_add_to_bag_submit', { selectedProduct });
    },
    [track],
  );

  const onOneClickAddToBagError = useCallback<OnOneClickAddToBagError>(
    (errorMessage) => {
      track('menu.one_click_add_to_bag_fail', { message: errorMessage });
    },
    [track],
  );

  const { addProductToBag, addingProductIds } = useOneClickAddToBag({
    restaurantSlug,
    deliveryDetails: deliveryDetailsData,
    onSuccess: onOneClickAddToBagSuccess,
    onError: onOneClickAddToBagError,
  });

  // NOTE: `addProductToBag` is rebuilt whenever the cart changes, so cards
  //       receive a permanently stable wrapper instead.
  const addProductToBagRef = useRef(addProductToBag);
  addProductToBagRef.current = addProductToBag;

  const handleAddProductToBag = useCallback<typeof addProductToBag>(
    (product, quantity) => addProductToBagRef.current(product, quantity),
    [],
  );

  /**
   * Card telemetry lives here rather than in the cards themselves. The
   * telemetry context value carries `isInitialized`, `mixpanelFeatureFlags`
   * and `user`, so a card subscribing to it re-renders whenever any of those
   * resolve — once per card, across the whole menu.
   */
  const handleProductCardPress = useCallback(
    (product: MenuContentProduct) => {
      const selectedProduct = telemetryProductFromInput({ ...product, location: restaurantSlug });

      trackEvent({
        name: 'pdp.view',
        payload: {
          entryPoint: 'menu',
          selectedProduct,
        },
      });
    },
    [restaurantSlug, trackEvent],
  );

  const handleProductDetailsLinkPress = useCallback(
    (product: MenuContentProduct) => {
      trackEvent({
        name: 'product-details.menu-card.link.tapped',
        payload: {
          baseProductSlug: product.baseProduct.slug,
          productId: product.id,
          productName: product.name,
        },
      });
    },
    [trackEvent],
  );

  const {
    isDietaryPreferencesModalVisible,
    showDietaryPreferencesModal,
    hideDietaryPreferencesModal,
  } = useMenuContentDietaryPreferencesModal();

  const [isShowingTopDownCategories, setIsShowingTopDownCategories] = useState(
    shouldRenderTopDownCategoriesModal || shouldShowTopDownCategoriesViewInitially,
  );

  // ─── Refs ────────────────────────────────────────────────────────────

  const listRef = useRef<FlashListRef<MenuContentListRow>>(null);
  const scrollOffset = useSharedValue(0);
  const navScrollViewRef = useRef<ScrollView>(null);

  // ─── Derived ─────────────────────────────────────────────────────────

  const shouldShowTopDownCategoriesView =
    shouldRenderMenuRedesign && (isShowingTopDownCategories || view === MENU_VIEWS.CATEGORIES);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const { generateCustomizationHref, setTargetParam, defaultActiveTargetId, changeLocation } =
    useMenuContentNavigation({
      orderChannel,
      restaurantSlug,
      deliveryDetailsData,
    });

  const onContactUs = useCallback(() => {
    trackEvent({ name: 'menu.contact_us' });
  }, [trackEvent]);

  const hideTopDownCategories = useCallback(() => {
    setIsShowingTopDownCategories(false);
  }, []);

  const showTopDownCategories = useCallback(() => {
    setIsShowingTopDownCategories(true);
  }, []);

  const listRows = useMemo(() => {
    return buildMenuContentListRows({
      categories: filteredCategories,
      reorderRailTargetId: shouldRenderReorderRailSection ? REORDER_RAIL_TARGET_ID : undefined,
    });
  }, [filteredCategories, shouldRenderReorderRailSection]);

  const fallbackDefaultTargetId = shouldRenderReorderRailSection
    ? REORDER_RAIL_TARGET_ID
    : filteredCategories?.[0]?.slug;
  const defaultTargetId = defaultActiveTargetId ?? fallbackDefaultTargetId;

  const {
    actorRef: scrollspyActorRef,
    initialScrollIndex,
    scrollTo,
    onViewableItemsChanged,
  } = useVirtualizedScrollspy({
    rows: listRows,
    getTargetId: getMenuContentListRowTargetId,
    listRef,
    defaultTargetId,
    onActiveTargetIdChange: setTargetParam,
  });

  const activeTargetId = useScrollspyActiveTargetId(scrollspyActorRef);

  const handleListScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      scrollOffset.value = event.nativeEvent.contentOffset.y;
    },
    [scrollOffset],
  );

  const onNavItemPress = useCallback(
    (targetId: string) => {
      if (targetId === REORDER_RAIL_TARGET_ID) {
        scrollTo(targetId);

        trackEvent({
          name: 'menu.jump_nav',
          payload: { id: restaurantSlug, tabName: REORDER_RAIL_TARGET_ID },
        });

        return;
      }

      const targetCategory = filteredCategories.find((category) => category.slug === targetId);

      if (!targetCategory) return;

      scrollTo(targetId);

      trackEvent({
        name: 'menu.jump_nav',
        payload: { id: restaurantSlug, tabName: targetCategory.name },
      });
    },
    [filteredCategories, restaurantSlug, scrollTo, trackEvent],
  );

  const handleTopDownViewCategoryPress = useCallback(
    (targetId: string) => {
      scrollTo(targetId);
      setIsShowingTopDownCategories(false);

      router.setParams({ category: targetId, view: MENU_VIEWS.MENU });
    },
    [router, scrollTo],
  );

  const onDietaryPreferencesModalClose = useCallback(() => {
    hideDietaryPreferencesModal();
    void setHasOpenedDietaryPreferencesState();
  }, [hideDietaryPreferencesModal, setHasOpenedDietaryPreferencesState]);

  // NOTE: In case of `undefined`, the default `goBack` action will be used.
  const customBackButtonHandler =
    shouldRenderTopDownCategoriesModal && !isShowingTopDownCategories
      ? showTopDownCategories
      : undefined;

  const openBag = useCallback(() => {
    router.navigate('/bag');
  }, [router]);

  const trackMenuViewBrazeEvent = useCallback(() => {
    logCustomEvent('menu.view');
  }, [logCustomEvent]);

  const trackMenuViewRadarEvent = useCallback(() => {
    trackRadarMenuViewed(restaurantSlug);
  }, [restaurantSlug]);

  useDeliveryOrderInFlight();
  useReorderIfRequired(restaurantSlug, deliveryDetailsData?.addressId);

  useSetLastInteractedMenuStore({
    restaurantId,
    restaurantName,
    restaurantSlug,
    addressId: deliveryDetailsData?.addressId,
    deliveryFee: deliveryDetailsData?.deliveryFee,
    vendor: deliveryDetailsData?.vendor,
    vendorRestaurantId: deliveryDetailsData?.vendorRestaurantId,
  });

  // ─── Effects ─────────────────────────────────────────────────────────

  useTrackEventOnFocus(
    {
      name: 'menu.view',
      payload: {
        restaurant: telemetryLocationFromInput(restaurantData),
        products: menuTelemetryProducts,
      },
    },
    { once: true },
  );

  useCustomScreenTitle(
    formatMessage(messages.orderChannelAndLocationText, {
      order_channel: orderChannel,
      location: locationName,
    }),
  );

  useFocusEffect(trackMenuViewBrazeEvent);
  useFocusEffect(trackMenuViewRadarEvent);

  // ─── Styles ──────────────────────────────────────────────────────────

  const menuContentContainerStyles = [
    styles.container,
    shouldRenderMenuRedesign ? styles.menuRedesignContentContainer : undefined,
  ];

  // ─────────────────────────────────────────────────────────────────────

  const shouldRenderDietaryPreferencesToggle =
    !isResolvingDietaryPreferencesState && !hasOpenedDietaryPreferences;

  const renderProductCard = useCallback(
    (product: MenuContentProduct) => {
      const productDetailsContentDataItem = productDetailsContentData?.get(product.baseProduct.slug); // prettier-ignore
      const productDetailsData = productDetailsContentDataItem?.fields;
      const hasProductDetails = Boolean(productDetailsData?.isEnabled);

      const cardHoverImage = productDetailsData?.cardHoverImage;
      const cardHoverStaticImage = productDetailsData?.cardHoverStaticImage;
      const carousel = productDetailsData?.carousel;

      const hoverImageUrl = cardHoverImage?.fields?.file?.url;
      const hoverStaticImageUrl = cardHoverStaticImage?.fields?.file?.url;
      const hoverImageAlt = cardHoverImage?.fields?.title;
      const caloriesOverrideEntry = caloriesOverrides?.[product.baseProduct.slug];

      const restrictionNotice = getRestrictionNotice({
        allergenProperties: product.allergenProperties,
      });

      return (
        <MenuContentProductCard
          key={product.id}
          product={product}
          hasProductDetails={hasProductDetails}
          generateHref={generateCustomizationHref}
          imageFetchingPriority={shouldShowTopDownCategoriesView ? 'low' : 'normal'}
          hoverImageUrl={hoverImageUrl}
          hoverStaticImageUrl={hoverStaticImageUrl}
          hoverImageAlt={hoverImageAlt}
          carousel={carousel}
          calorieOverride={caloriesOverrideEntry?.caloriesOverride}
          calorieOverrideText={caloriesOverrideEntry?.caloriesOverrideText}
          isLineageKeyCopyModeEnabled={isLineageKeyCopyModeEnabled}
          restrictionNotice={restrictionNotice}
          addProductToBag={handleAddProductToBag}
          isAddingProductToBag={addingProductIds.has(product.id)}
          onCardPress={handleProductCardPress}
          onProductDetailsLinkPress={handleProductDetailsLinkPress}
        />
      );
    },
    [
      addingProductIds,
      caloriesOverrides,
      handleAddProductToBag,
      generateCustomizationHref,
      getRestrictionNotice,
      handleProductCardPress,
      handleProductDetailsLinkPress,
      isLineageKeyCopyModeEnabled,
      productDetailsContentData,
      shouldShowTopDownCategoriesView,
    ],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      {shouldRenderMenuRedesign ? <ScreenHeader disclaimer={disclaimer} rounded={false} /> : null}

      <View style={styles.container}>
        <View
          style={menuContentContainerStyles}
          accessibilityElementsHidden={shouldShowTopDownCategoriesView}
          importantForAccessibility={
            shouldShowTopDownCategoriesView ? 'no-hide-descendants' : 'auto'
          }
          pointerEvents={shouldShowTopDownCategoriesView ? 'none' : 'auto'}
        >
          {/* ─── Navigation Header ──────────────────────────────────────── */}

          {shouldRenderMenuRedesign ? null : (
            <MenuContentNavigationHeader
              orderChannel={orderChannel}
              locationName={locationName}
              onBackBtnPressOverride={customBackButtonHandler}
            />
          )}

          {/* ─── Header ───────────────────────────────────────────────────── */}

          <MenuContentHeader
            menuGridScrollOffsetSV={scrollOffset}
            restaurantData={restaurantData}
            locationName={locationName}
            orderChannel={orderChannel}
            deliveryDetailsData={deliveryDetailsData}
            onContactUs={onContactUs}
            shouldHideDeliveryFeeDisclosure={Boolean(disclaimer)}
            {...locationHeaderA11ySelector.props}
          >
            <MenuCategoriesGrid.Nav ref={navScrollViewRef} testID="menu-content.categories-nav">
              {shouldRenderReorderRailSection ? (
                <MenuCategoriesGrid.NavItem
                  activePalette={isNavbarV2Enabled ? 'spinach' : 'primary'}
                  category={formatMessage(messages.reorderRailNavLabel)}
                  targetId={REORDER_RAIL_TARGET_ID}
                  navScrollViewRef={navScrollViewRef}
                  onPress={onNavItemPress}
                  isActive={activeTargetId === REORDER_RAIL_TARGET_ID}
                />
              ) : null}

              {filteredCategories.map((category) => (
                <MenuCategoriesGrid.NavItem
                  activePalette={isNavbarV2Enabled ? 'spinach' : 'primary'}
                  key={category.id}
                  category={category.name}
                  targetId={category.slug}
                  navScrollViewRef={navScrollViewRef}
                  onPress={onNavItemPress}
                  isActive={activeTargetId === category.slug}
                />
              ))}
            </MenuCategoriesGrid.Nav>
          </MenuContentHeader>

          {/* ─── Categories ───────────────────────────────────────────────── */}

          <MenuContentCategories
            ref={listRef}
            rows={listRows}
            onScroll={handleListScroll}
            initialScrollIndex={initialScrollIndex}
            onViewableItemsChanged={onViewableItemsChanged}
            categoriesBadges={categoriesBadges}
            reorderRailOrders={reorderRailOrders}
            restaurantData={restaurantData}
            orderChannel={orderChannel}
            deliveryDetailsData={deliveryDetailsData}
            onDietaryPreferencesTogglePress={
              shouldRenderDietaryPreferencesToggle ? showDietaryPreferencesModal : undefined
            }
            generateCustomizationHref={generateCustomizationHref}
            renderProductCard={renderProductCard}
            hasBottomDisclaimer={Boolean(disclaimer)}
          />
        </View>

        {shouldShowTopDownCategoriesView ? (
          <MenuContentTopDownView
            categories={filteredCategories}
            categoriesBadges={categoriesBadges}
            onCategoryPress={handleTopDownViewCategoryPress}
            withNavbarSpacing={isNavbarV2Enabled}
            recentOrdersTargetId={
              shouldRenderReorderRailSection ? REORDER_RAIL_TARGET_ID : undefined
            }
          />
        ) : null}
      </View>

      {minWidth.isSM && disclaimer ? (
        <View style={styles.bottomDisclaimerContainer}>
          <GarnishScreenHeader.Disclaimer disclaimer={disclaimer} />
        </View>
      ) : null}

      {/* ─── Modals ─────────────────────────────────────────────────────── */}

      <MenuContentDietaryPreferencesModal
        isVisible={isDietaryPreferencesModalVisible}
        onClose={onDietaryPreferencesModalClose}
      />

      {isAcceptingOrders ? null : (
        <MenuOnlineOrderingUnavailable.Container>
          <MenuOnlineOrderingUnavailable.Dialog withNavbarSpacing={isNavbarV2Enabled}>
            <MenuOnlineOrderingUnavailable.Message message={notAcceptingOrdersReason} />
            <MenuOnlineOrderingUnavailable.Button onPress={changeLocation} />
          </MenuOnlineOrderingUnavailable.Dialog>
        </MenuOnlineOrderingUnavailable.Container>
      )}

      {shouldRenderTopDownCategoriesModal ? (
        <MenuContentTopDownModal
          orderChannel={orderChannel}
          locationName={locationName}
          categories={filteredCategories}
          categoriesBadges={categoriesBadges}
          isVisible={isShowingTopDownCategories}
          hideModal={hideTopDownCategories}
          onCategoryPress={scrollTo}
          recentOrdersTargetId={shouldRenderReorderRailSection ? REORDER_RAIL_TARGET_ID : undefined}
        />
      ) : null}

      {/* ─── Floating Buttons ───────────────────────────────────────────── */}

      <FadeView show={shouldShowBagFloatingButton}>
        <MenuFloatingButtons.Bag lineItemsCount={lineItemsCount} onPress={openBag} />
      </FadeView>
    </>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getMenuHref(forwardedParams: MenuLocalParams): Href {
  if (checkIfDeliveryMenuParams(forwardedParams)) {
    return { pathname: MENU_PATHNAMES.delivery, params: forwardedParams };
  }

  return { pathname: MENU_PATHNAMES.standard, params: forwardedParams };
}

function checkIfDeliveryMenuParams(
  params: MenuLocalParams,
): params is Extract<MenuLocalParams, { addressId: string }> {
  return 'addressId' in params;
}

// ─── Constants ───────────────────────────────────────────────────────────────

/**
 * Scrollspy target ID of the "order again" rail, which precedes the first
 * category when the customer has recent orders.
 */
const REORDER_RAIL_TARGET_ID = 'order-again';

const MENU_PATHNAMES = {
  standard: '/[restaurantSlug]/menu',
  delivery: '/delivery/[addressId]/menu',
} as const satisfies Record<string, RoutePath>;

const MENU_SCREEN_NAMES = [
  '[restaurantSlug]/menu/index',
  'delivery/[addressId]/menu/index',
] as const satisfies readonly StackRouteName<MenuRoute>[];

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  deliveryEtaText: {
    defaultMessage: 'Delivers in {eta} minutes',
    description: 'Menu | Header | Delivery ETA text',
  },
  orderChannelAndLocationText: {
    defaultMessage: `{order_channel, select,
      pickup {Pickup from {location}}
      delivery {Delivery to {location}}
      outpost {Outpost at {location}}
      other {}
    }`,
    description: 'Menu | Navigation Header | Order channel and location text',
  },
  reorderRailNavLabel: {
    defaultMessage: 'Order again',
    description: 'Menu | Categories Nav | Reorder rail nav item label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  menuRedesignContentContainer: {
    paddingTop: theme.spacing['4'],
  },
  bottomDisclaimerContainer: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: theme.zIndex.fixed,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type MenuContentReadyProps = {
  restaurantData: MenuContentRestaurantData;
  productDetailsContentData: ProductDetailsContentData;
  categoriesBadges: CategoriesBadges;
  caloriesOverrides: CaloriesOverrides;
  deliveryDetailsData?: MenuContentDeliveryDetailsData;
  orderChannel: MenuContentOrderChannel;
  locationName: string;
  reorderRailOrders: ReturnType<typeof useMenuReorderRailOrders>;
};

type MenuRoute = (typeof MENU_PATHNAMES)[keyof typeof MENU_PATHNAMES];
type MenuLocalParams = RouteParams<MenuRoute>;
type MenuSearchParams = {
  referrer?: string;
  category?: string;
  view?: MenuView;
};
type MenuView = (typeof MENU_VIEWS)[keyof typeof MENU_VIEWS];

type ProductDetailsContentData = ReturnType<
  typeof useProductDetailsContentData
>['productDetailsContentData'];

type CategoriesBadges = ReturnType<typeof useMenuContent>['mappedContent']['categoriesBadges'];
type CaloriesOverrides = ReturnType<typeof useMenuContent>['mappedContent']['caloriesOverrides'];

type StackRouteName<R extends RoutePath> = R extends `/${infer Path}` ? `${Path}/index` : never;

type OnOneClickAddToBagSuccess = NonNullable<OneClickAddToBagParams['onSuccess']>;
type OnOneClickAddToBagError = NonNullable<OneClickAddToBagParams['onError']>;
type OneClickAddToBagParams = Parameters<typeof useOneClickAddToBag>['0'];
